/**
 * Tipos e validação do artifact `chart` (contrato v1, 15/09/2026).
 *
 * O payload chega do SSE por cast cru. O contrato manda que versão desconhecida ou payload
 * inválido virem "gráfico não suportado" **preservando o texto da conversa** — nunca uma
 * exceção que derrube o turno. Por isso o parse devolve um resultado discriminado em vez de
 * lançar, e o componente decide entre desenhar e mostrar o aviso.
 *
 * O backend já valida tudo antes de emitir. O que é revalidado aqui é o subconjunto que o
 * renderer não sobrevive ou que o faria mentir: eixo Y que não resolve, par X/Y desalinhado,
 * log de valor não positivo. Não é uma segunda implementação do validador do backend.
 */

export type ChartType = 'line' | 'scatter' | 'bar' | 'area';
export type ChartScale = 'linear' | 'log10';

export interface ChartXAxis {
  label: string;
  unit: string | null;
  /** `time` é duração em segundos relativa ao log — nunca epoch nem horário com fuso. */
  kind: 'numeric' | 'time' | 'category';
  scale: ChartScale;
}

export interface ChartYAxis {
  id: string;
  label: string;
  unit: string | null;
  scale: ChartScale;
  side: 'left' | 'right';
}

export interface ChartSeries {
  name: string;
  type: ChartType;
  /** Referência a `ChartYAxis.id`. Observações e regressão da mesma grandeza compartilham o ID. */
  y_axis: string;
  /** Pareado com `y` pelo índice. Cada série tem o próprio X: não alinhe séries entre si. */
  x: number[] | string[];
  /** `null` é lacuna — não é zero e não deve ser conectada. */
  y: (number | null)[];
}

export interface ChartPayload {
  schema_version: 1;
  chart_type: ChartType;
  title: string | null;
  x_axis: ChartXAxis;
  y_axes: ChartYAxis[];
  series: ChartSeries[];
  points: number;
  source_points: number | null;
  source: 'execute_python';
  log_id: string | null;
  file_name: string | null;
}

/** Motivo pelo qual o gráfico não pode ser desenhado. Vira chave de i18n no componente. */
export type ChartRejection =
  | 'not_an_object'
  | 'unsupported_version'
  | 'no_series'
  | 'no_y_axes'
  | 'unknown_y_axis'
  | 'misaligned_pairs'
  | 'invalid_x'
  | 'category_requires_bar'
  | 'nonpositive_on_log'
  | 'too_few_points';

export type ChartParseResult =
  | { ok: true; chart: ChartPayload }
  | { ok: false; reason: ChartRejection };

const CHART_TYPES: ReadonlySet<string> = new Set(['line', 'scatter', 'bar', 'area']);

const fail = (reason: ChartRejection): ChartParseResult => ({ ok: false, reason });

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

export function parseChartArtifact(payload: unknown): ChartParseResult {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return fail('not_an_object');
  }

  const p = payload as Partial<ChartPayload>;

  // Discriminador do contrato: só a versão 1 é conhecida. Versão nova sem renderer novo
  // deve aparecer como não suportada, não como gráfico errado.
  if (p.schema_version !== 1) return fail('unsupported_version');

  const xAxis = p.x_axis;
  if (!xAxis || typeof xAxis !== 'object') return fail('not_an_object');

  const yAxes = Array.isArray(p.y_axes) ? p.y_axes : [];
  if (!yAxes.length) return fail('no_y_axes');

  const series = Array.isArray(p.series) ? p.series : [];
  if (!series.length) return fail('no_series');

  if (!CHART_TYPES.has(p.chart_type as string)) return fail('not_an_object');

  // Categoria é exclusiva de barras: num scatter não existe ordem definida para strings.
  if (xAxis.kind === 'category' && p.chart_type !== 'bar') {
    return fail('category_requires_bar');
  }

  const axisIds = new Set(yAxes.map((a) => a.id));
  const axisById = new Map(yAxes.map((a) => [a.id, a]));

  for (const s of series) {
    if (!s || !Array.isArray(s.x) || !Array.isArray(s.y)) return fail('misaligned_pairs');
    if (s.x.length !== s.y.length) return fail('misaligned_pairs');
    if (!axisIds.has(s.y_axis)) return fail('unknown_y_axis');

    const type = (s.type ?? p.chart_type) as ChartType;
    // Uma linha ou área com um ponto só não é desenhável: não há segmento.
    const minPoints = type === 'line' || type === 'area' ? 2 : 1;
    if (s.x.length < minPoints) return fail('too_few_points');

    const categorical = xAxis.kind === 'category';
    for (const x of s.x) {
      if (x === null || x === undefined) return fail('invalid_x');
      if (categorical ? typeof x !== 'string' : !isFiniteNumber(x)) return fail('invalid_x');
      if (!categorical && xAxis.scale === 'log10' && (x as number) <= 0) {
        return fail('nonpositive_on_log');
      }
    }

    const yScale = axisById.get(s.y_axis)!.scale;
    for (const y of s.y) {
      if (y === null) continue; // lacuna declarada — legítima
      if (!isFiniteNumber(y)) return fail('misaligned_pairs');
      if (yScale === 'log10' && y <= 0) return fail('nonpositive_on_log');
    }
  }

  return { ok: true, chart: p as ChartPayload };
}
